import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, BookOpen, Award, TrendingUp, Calendar, MessageSquare,
  CheckCircle, Clock, AlertCircle, Star, ChevronRight, LogOut,
  BarChart3, Target, Briefcase, GraduationCap, Heart, Home,
  FileText, Settings, Bell, Menu, X, Building, UserCheck,
  Send, Download, Eye, Plus, Search, Filter, Mail, Phone,
  ArrowRight, RefreshCw, ChevronDown, MoreVertical
} from 'lucide-react';
import CNESSLogo from '../components/CNESSLogo';

const MentorDashboard = () => {
  const [mentorData, setMentorData] = useState(null);
  const [stats, setStats] = useState(null);
  const [assignedOrgs, setAssignedOrgs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeModule, setActiveModule] = useState('dashboard');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgProgress, setOrgProgress] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('all');
  const navigate = useNavigate();

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [profileRes, statsRes, orgsRes, sessionsRes, messagesRes, resourcesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/mentor/profile/${userData.id}`),
        fetch(`${BACKEND_URL}/api/mentor/dashboard-stats/${userData.id}`),
        fetch(`${BACKEND_URL}/api/mentor/assigned-organizations/${userData.id}`),
        fetch(`${BACKEND_URL}/api/mentor/sessions/${userData.id}`),
        fetch(`${BACKEND_URL}/api/mentor/messages/${userData.id}`),
        fetch(`${BACKEND_URL}/api/mentor/resources`)
      ]);

      if (profileRes.ok) setMentorData(await profileRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
      if (orgsRes.ok) setAssignedOrgs(await orgsRes.json());
      if (sessionsRes.ok) setSessions(await sessionsRes.json());
      if (messagesRes.ok) setMessages(await messagesRes.json());
      if (resourcesRes.ok) setResources(await resourcesRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgProgress = async (orgId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/mentor/organization-progress/${orgId}`);
      if (res.ok) {
        const data = await res.json();
        setOrgProgress(data);
      }
    } catch (error) {
      console.error('Error fetching org progress:', error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const markMessageRead = async (messageId) => {
    try {
      await fetch(`${BACKEND_URL}/api/mentor/messages/${messageId}/read`, { method: 'PUT' });
      setMessages(messages.map(m => m.id === messageId ? {...m, read: true} : m));
    } catch (error) {
      console.error('Error marking message read:', error);
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'organizations', label: 'My Organizations', icon: Building },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
    { id: 'progress', label: 'Progress Tracking', icon: BarChart3 },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: messages.filter(m => !m.read).length },
    { id: 'resources', label: 'Resources', icon: BookOpen },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const statsCards = stats ? [
    { label: 'Assigned Organizations', value: stats.assigned_organizations, icon: Users, color: 'bg-blue-500', bgColor: 'bg-blue-50', trend: '+2' },
    { label: 'Sessions Completed', value: stats.completed_sessions, icon: CheckCircle, color: 'bg-green-500', bgColor: 'bg-green-50', trend: '+5' },
    { label: 'Hours Mentored', value: Math.round(stats.hours_mentored), icon: Clock, color: 'bg-purple-500', bgColor: 'bg-purple-50', trend: '+12' },
    { label: 'Avg Rating', value: stats.avg_rating, icon: Star, color: 'bg-amber-500', bgColor: 'bg-amber-50', trend: '+0.2' }
  ] : [];

  const filteredOrgs = assignedOrgs.filter(org => {
    const matchesSearch = org.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterLevel === 'all' || org.certification_level === filterLevel;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading mentor dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-purple-800 via-purple-900 to-indigo-900 text-white transition-all duration-300 flex flex-col fixed h-full z-50`}>
        <div className="p-4 border-b border-purple-700/50">
          <div className="flex items-center justify-between">
            {sidebarOpen ? (
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-lg p-1.5">
                  <CNESSLogo variant="mark" />
                </div>
                <div>
                  <h1 className="font-bold text-lg">CNESS</h1>
                  <p className="text-xs text-purple-300">Mentor Portal</p>
                </div>
              </div>
            ) : (
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mx-auto shadow-lg p-1">
                <CNESSLogo variant="mark" className="!h-7" />
              </div>
            )}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {sidebarOpen && (
          <div className="p-4 border-b border-purple-700/50">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">{(mentorData?.username || 'M')[0].toUpperCase()}</span>
              </div>
              <div>
                <p className="font-semibold">{mentorData?.username || 'Mentor'}</p>
                <span className="text-xs bg-purple-500/30 px-2 py-1 rounded-full">Certified Mentor</span>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveModule(item.id)}
                className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'justify-center'} py-3 rounded-xl transition-all duration-200 ${
                  activeModule === item.id
                    ? 'bg-white/20 text-white shadow-lg'
                    : 'text-purple-200 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5" />
                {sidebarOpen && (
                  <>
                    <span className="ml-3 font-medium">{item.label}</span>
                    {item.badge > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-purple-700/50">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center ${sidebarOpen ? 'px-4' : 'justify-center'} py-3 rounded-xl text-purple-200 hover:bg-red-500/20 hover:text-red-300 transition-all`}
          >
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        <header className="bg-white shadow-sm border-b border-gray-200 px-8 py-4 sticky top-0 z-40">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {navItems.find(i => i.id === activeModule)?.label || 'Dashboard'}
              </h1>
              <p className="text-sm text-gray-500">Welcome back, {mentorData?.username || 'Mentor'}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={fetchAllData}
                className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                title="Refresh data"
              >
                <RefreshCw className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 relative transition-colors">
                <Bell className="w-5 h-5 text-gray-600" />
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>

        <div className="p-8">
          {/* Dashboard Module */}
          {activeModule === 'dashboard' && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statsCards.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <div key={index} className={`${stat.bgColor} rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className={`w-14 h-14 ${stat.color} rounded-xl flex items-center justify-center shadow-lg`}>
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-green-600 text-sm font-semibold bg-green-100 px-2 py-1 rounded-full">
                          {stat.trend}
                        </span>
                      </div>
                      <p className="text-4xl font-black text-gray-900">{stat.value}</p>
                      <p className="text-gray-600 font-medium mt-1">{stat.label}</p>
                    </div>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                      <Building className="w-6 h-6 mr-2 text-purple-600" />
                      Assigned Organizations
                    </h2>
                    <button 
                      onClick={() => setActiveModule('organizations')}
                      className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center"
                    >
                      View All <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                  <div className="space-y-4">
                    {assignedOrgs.slice(0, 4).map((org, index) => (
                      <div 
                        key={index} 
                        className="border border-gray-200 rounded-xl p-4 hover:border-purple-300 hover:bg-purple-50/30 transition-all cursor-pointer"
                        onClick={() => {
                          setSelectedOrg(org);
                          fetchOrgProgress(org.id);
                          setActiveModule('progress');
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-gray-900">{org.name}</h3>
                            <p className="text-sm text-gray-500">{org.industry || 'Technology'} • {org.focus_pillar}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            org.certification_level === 'Luminary' ? 'bg-purple-100 text-purple-700' :
                            org.certification_level === 'Inspired' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {org.certification_level}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2.5 rounded-full transition-all duration-500" 
                              style={{ width: `${org.progress}%` }} 
                            />
                          </div>
                          <span className="text-sm font-bold text-gray-700">{org.progress}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center mb-6">
                    <Calendar className="w-6 h-6 mr-2 text-purple-600" />
                    Upcoming Sessions
                  </h2>
                  <div className="space-y-4">
                    {sessions.filter(s => s.status === 'upcoming').slice(0, 3).map((session) => (
                      <div key={session.id} className="border border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="font-semibold text-gray-900 text-sm">{session.organization_name}</h4>
                          <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-lg text-xs font-semibold">
                            {session.status}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{session.topic}</p>
                        <div className="flex items-center text-xs text-gray-400">
                          <Calendar className="w-3 h-3 mr-1" />
                          {session.date} at {session.time}
                        </div>
                      </div>
                    ))}
                    {sessions.filter(s => s.status === 'upcoming').length === 0 && (
                      <p className="text-gray-500 text-center py-4">No upcoming sessions</p>
                    )}
                  </div>
                  <button 
                    onClick={() => setShowSessionModal(true)}
                    className="w-full mt-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center justify-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Schedule New Session
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Organizations Module */}
          {activeModule === 'organizations' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">My Assigned Organizations</h2>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search organizations..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={filterLevel}
                    onChange={(e) => setFilterLevel(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="all">All Levels</option>
                    <option value="Luminary">Luminary</option>
                    <option value="Inspired">Inspired</option>
                    <option value="Aspiring">Aspiring</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredOrgs.map((org) => (
                  <div 
                    key={org.id} 
                    className="border border-gray-200 rounded-xl p-5 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => {
                      setSelectedOrg(org);
                      fetchOrgProgress(org.id);
                      setActiveModule('progress');
                    }}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-lg">
                        {org.name?.charAt(0)}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        org.certification_level === 'Luminary' ? 'bg-purple-100 text-purple-700' :
                        org.certification_level === 'Inspired' ? 'bg-green-100 text-green-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {org.certification_level}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors">{org.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{org.industry || 'Technology'}</p>
                    
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">Progress</span>
                        <span className="font-semibold text-gray-900">{org.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full" 
                          style={{ width: `${org.progress}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Focus: {org.focus_pillar}</span>
                      <ArrowRight className="w-4 h-4 text-purple-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sessions Module */}
          {activeModule === 'sessions' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Mentoring Sessions</h2>
                <button 
                  onClick={() => setShowSessionModal(true)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  New Session
                </button>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-blue-600" />
                    Upcoming Sessions
                  </h3>
                  <div className="space-y-4">
                    {sessions.filter(s => s.status === 'upcoming').map((session) => (
                      <div key={session.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">{session.organization_name}</h4>
                            <p className="text-sm text-gray-600">{session.topic}</p>
                          </div>
                          <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg text-xs font-semibold">
                            Upcoming
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {session.date} at {session.time}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock className="w-4 h-4 mr-1" />
                            {session.duration} min
                          </div>
                        </div>
                      </div>
                    ))}
                    {sessions.filter(s => s.status === 'upcoming').length === 0 && (
                      <p className="text-gray-500 text-center py-8">No upcoming sessions scheduled</p>
                    )}
                  </div>
                </div>
                
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                    Completed Sessions
                  </h3>
                  <div className="space-y-4">
                    {sessions.filter(s => s.status === 'completed').map((session) => (
                      <div key={session.id} className="border border-gray-200 rounded-xl p-4 hover:border-green-300 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">{session.organization_name}</h4>
                            <p className="text-sm text-gray-600">{session.topic}</p>
                          </div>
                          <span className="bg-green-100 text-green-700 px-3 py-1 rounded-lg text-xs font-semibold">
                            Completed
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-1" />
                            {session.date}
                          </div>
                          {session.rating && (
                            <div className="flex items-center text-sm text-amber-500">
                              <Star className="w-4 h-4 mr-1 fill-current" />
                              {session.rating}/5
                            </div>
                          )}
                        </div>
                        {session.feedback && (
                          <p className="text-sm text-gray-600 mt-2 italic">"{session.feedback}"</p>
                        )}
                      </div>
                    ))}
                    {sessions.filter(s => s.status === 'completed').length === 0 && (
                      <p className="text-gray-500 text-center py-8">No completed sessions yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Progress Tracking Module */}
          {activeModule === 'progress' && (
            <div className="space-y-6">
              {selectedOrg ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <button 
                        onClick={() => setSelectedOrg(null)}
                        className="text-purple-600 hover:text-purple-700 text-sm mb-2 flex items-center"
                      >
                        ← Back to organizations
                      </button>
                      <h2 className="text-2xl font-bold text-gray-900">{selectedOrg.name}</h2>
                      <p className="text-gray-600">{selectedOrg.industry} • {selectedOrg.certification_level}</p>
                    </div>
                    <button 
                      onClick={() => setShowSessionModal(true)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold"
                    >
                      Schedule Session
                    </button>
                  </div>

                  {orgProgress && (
                    <>
                      <div className="bg-white rounded-2xl shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-bold text-gray-900">Overall Progress</h3>
                          <div className="text-3xl font-black text-purple-600">{orgProgress.overall_score}%</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-4 rounded-full transition-all duration-500" 
                            style={{ width: `${orgProgress.overall_score}%` }}
                          />
                        </div>
                      </div>

                      <div className="bg-white rounded-2xl shadow-sm p-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-6">Pillar Progress</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {orgProgress.pillar_progress?.map((pillar) => (
                            <div key={pillar.pillar_number} className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-semibold text-gray-900">{pillar.pillar_name}</span>
                                <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${
                                  pillar.status === 'strong' ? 'bg-green-100 text-green-700' :
                                  pillar.status === 'developing' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {pillar.status}
                                </span>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full ${
                                      pillar.status === 'strong' ? 'bg-green-500' :
                                      pillar.status === 'developing' ? 'bg-yellow-500' :
                                      'bg-red-500'
                                    }`}
                                    style={{ width: `${pillar.score}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-gray-700 w-12">{pillar.score}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-2xl shadow-sm p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Select an Organization</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assignedOrgs.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => {
                          setSelectedOrg(org);
                          fetchOrgProgress(org.id);
                        }}
                        className="border border-gray-200 rounded-xl p-4 hover:border-purple-400 hover:bg-purple-50 transition-all text-left"
                      >
                        <h3 className="font-semibold text-gray-900">{org.name}</h3>
                        <p className="text-sm text-gray-500">{org.certification_level}</p>
                        <div className="flex items-center mt-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{ width: `${org.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold ml-2">{org.progress}%</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Messages Module */}
          {activeModule === 'messages' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Inbox</h3>
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-semibold">
                    {messages.filter(m => !m.read).length} unread
                  </span>
                </div>
                <div className="space-y-2">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id}
                      onClick={() => markMessageRead(msg.id)}
                      className={`p-3 rounded-xl cursor-pointer transition-all ${
                        !msg.read ? 'bg-purple-50 border-l-4 border-purple-500' : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-gray-900 text-sm">{msg.sender_name}</span>
                        {!msg.read && <span className="w-2 h-2 bg-purple-500 rounded-full"></span>}
                      </div>
                      <p className="text-sm text-gray-600 font-medium">{msg.subject}</p>
                      <p className="text-xs text-gray-400 mt-1 truncate">{msg.content}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-900">Compose Message</h3>
                </div>
                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                      <option>Select organization...</option>
                      {assignedOrgs.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                    <input 
                      type="text" 
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Enter subject..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                    <textarea 
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      placeholder="Type your message..."
                    />
                  </div>
                  <button 
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* Resources Module */}
          {activeModule === 'resources' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Mentoring Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {resources.map((resource) => (
                  <div key={resource.id} className="border border-gray-200 rounded-xl p-5 hover:border-purple-400 hover:shadow-md transition-all">
                    <div className="flex items-start justify-between mb-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        resource.type === 'pdf' ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                        <FileText className={`w-6 h-6 ${resource.type === 'pdf' ? 'text-red-600' : 'text-blue-600'}`} />
                      </div>
                      <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-lg text-xs font-medium uppercase">
                        {resource.type}
                      </span>
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{resource.title}</h3>
                    <p className="text-sm text-gray-600 mb-4">{resource.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">{resource.downloads} downloads</span>
                      <button className="text-purple-600 hover:text-purple-700 font-medium text-sm flex items-center">
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Module */}
          {activeModule === 'settings' && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-6">Settings</h2>
              <div className="max-w-2xl space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Profile Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                      <input 
                        type="text" 
                        value={mentorData?.username || ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input 
                        type="email" 
                        value={mentorData?.email || ''}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                        disabled
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Notification Preferences</h3>
                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-purple-600 rounded" defaultChecked />
                      <span className="ml-3 text-gray-700">Email notifications for new messages</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-purple-600 rounded" defaultChecked />
                      <span className="ml-3 text-gray-700">Session reminders</span>
                    </label>
                    <label className="flex items-center">
                      <input type="checkbox" className="w-4 h-4 text-purple-600 rounded" />
                      <span className="ml-3 text-gray-700">Weekly progress reports</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Session Modal */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Schedule New Session</h3>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Organization</label>
                <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500">
                  <option>Select organization...</option>
                  {assignedOrgs.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
                <input 
                  type="text" 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Session topic..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input 
                    type="date" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                  <input 
                    type="time" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea 
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  placeholder="Session notes..."
                />
              </div>
              <div className="flex space-x-3">
                <button 
                  type="button"
                  onClick={() => setShowSessionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg"
                >
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentorDashboard;
